import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Pencil, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";

import { stripeUrl } from "common/const";
import {
  AnyPermissionsKey,
  PermissionsKeys,
} from "components/permissions/permission-keys";
import { EditPaymentMethodDialogue } from "components/settings/shared/billing-details/editPaymentDialogue";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { HubPlan } from "data/models";
import { addPaymentMethod } from "data/mutations-common";
import { fetchPaymentMethod } from "data/queries-common";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";

import { ChangeSummary } from "./change-summary";

interface ConfirmChangesCardProps {
  changes: ChangeSummary;
  canSubmit: boolean;
  isLoading: boolean;
  onSubmit: () => void;
  canCancel: boolean;
  onCancel: () => void;
  selectedPlan: HubPlan | null;
  trialSelected: boolean;
  newPlanIsPermitted: (keys: AnyPermissionsKey[]) => boolean;
}

export const ConfirmChangesCard = ({
  changes,
  canSubmit,
  isLoading,
  onSubmit,
  canCancel,
  onCancel,
  selectedPlan,
  newPlanIsPermitted,
}: ConfirmChangesCardProps) => {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const stripe = useStripe();
  const stripeElements = useElements();
  const [editingCC, setEditingCC] = useState(false);
  const queryClient = useQueryClient();

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  const showDetails =
    changes.changeType !== "no_selection" && changes.changeType !== "same";

  const paymentMethodValue = paymentMethodQuery.data
    ? `${toTitleCase(paymentMethodQuery.data.method)} .... ${
        paymentMethodQuery.data.last4
      } - ${paymentMethodQuery.data.expireMonth}/${
        paymentMethodQuery.data.expireYear
      }`
    : "No Payment Method";

  return (
    <Card className="border-primary/20 shadow-sm">
      <CardHeader>
        <CardTitle>Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="info">
          <AlertDescription>{changes.infoText}</AlertDescription>
        </Alert>

        {showDetails && (
          <>
            {changes.changeType === "downgrade" &&
              !newPlanIsPermitted([PermissionsKeys.membersUpdate]) && (
                <Alert variant="warning">
                  <TriangleAlert />
                  <AlertDescription>
                    <strong>WARNING,</strong> All members who are not the{" "}
                    <strong>owner</strong> will have their access to Hub
                    disabled when your subscription downgrades.
                  </AlertDescription>
                </Alert>
              )}

            <div className="flex flex-col gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Subscription
              </p>
              {changes.subscription.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.lhs}</span>
                  <span className="font-medium">{item.rhs}</span>
                </div>
              ))}
            </div>

            <Separator />

            <div className="flex flex-col gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Charges
              </p>
              {changes.charges
                .filter((x) => !x.hidden)
                .map((charge, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{charge.lhs}</span>
                    <span className="font-medium">{charge.rhs}</span>
                  </div>
                ))}
            </div>

            <Separator />

            <div className="flex items-baseline justify-between">
              <p className="text-base font-semibold">Total Due</p>
              <p className="text-xl font-bold">{changes.totalDue}</p>
            </div>

            {changes.trialChargeWarning && (
              <Alert variant="warning">
                <TriangleAlert />
                <AlertDescription>
                  {changes.trialChargeWarning}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {paymentMethodQuery.isSuccess && !selectedPlan?.free_plan && (
          <Fragment>
            <CardTitle>Payment Method</CardTitle>
            <Field>
              <FieldLabel htmlFor="paymentMethod">Card Details</FieldLabel>
              <InputGroup>
                <InputGroupInput
                  id="paymentMethod"
                  readOnly
                  value={paymentMethodValue}
                />
                <InputGroupAddon align="inline-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingCC(true)}
                  >
                    <Pencil />
                  </Button>
                </InputGroupAddon>
              </InputGroup>
            </Field>
            {paymentMethodQuery.data && paymentMethodQuery.data.expired && (
              <Alert variant="destructive">
                <TriangleAlert />
                <AlertDescription>
                  Your credit card has expired
                </AlertDescription>
              </Alert>
            )}
            <EditPaymentMethodDialogue
              open={editingCC}
              handleOnClose={handleOnClose}
              stripeUrl={stripeUrl}
              paymentMethodMutation={paymentMethodMutation}
            />
          </Fragment>
        )}
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button variant="ghost" disabled={!canCancel} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit || isLoading} onClick={onSubmit}>
          {isLoading ? (
            <>
              <Spinner />
              Purchase
            </>
          ) : (
            "Purchase"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
